import React from 'react'
import { useFieldArray } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { useControlledForm, useFormReset } from 'hooks'
import { mapPropsToInternalState } from './actions'
import { DEADLINE_FIELD, MESSAGES_FIELD } from './model'
import { getSchema } from './schema'

export const useSendTestsMessageForm = (state, handlers, mode?) => {
  const { candidates, company, name, deadline, templates, isOpen } = state
  const { onConfirm } = handlers

  const [term, setTerm] = React.useState(null)
  const [shouldReset, setShouldReset] = React.useState(false)

  const schema = getSchema()

  const internalState = React.useMemo(
    () =>
      mapPropsToInternalState({
        candidates,
        company,
        deadline: term || deadline,
        templates,
        name,
        mode,
      }),
    [candidates, company, deadline, templates, term, name, mode],
  )

  const handleFormData = React.useCallback(
    async data => {
      onConfirm(
        { candidates: data[MESSAGES_FIELD] },
        { onSuccess: () => setShouldReset(true) },
      )
    },
    [onConfirm],
  )

  const {
    control,
    formState,
    handleFormReset,
    handleFormSubmit,
    setValue,
    isDirtyOrInvalid,
    watch,
  } = useControlledForm({
    values: internalState,
    resolver: yupResolver(schema),
    handleFormData,
  })

  const { fields } = useFieldArray({ control, name: MESSAGES_FIELD })
  const fieldCount = fields.length

  useFormReset(
    { shouldReset: shouldReset && formState.isSubmitSuccessful },
    handleFormReset,
  )

  React.useEffect(() => {
    if (isOpen) {
      setShouldReset(false)
    }
  }, [isOpen])

  React.useEffect(() => {
    const subscription = watch((values, { name }) => {
      // name is like messages[12].deadline, so take the whole number
      const index = name ? /\d+/.exec(name)?.[0] : null

      if (index) {
        setTerm(values.messages[Number(index)]?.deadline)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [watch, isDirtyOrInvalid])

  // depends on the row count, not on fields, which setValue can replace
  React.useEffect(() => {
    if (term) {
      for (let index = 0; index < fieldCount; index += 1) {
        setValue(`${MESSAGES_FIELD}[${index}].${DEADLINE_FIELD}`, term)
      }
    }
  }, [term, fieldCount, setValue])

  return {
    state: { control, fields, formState, isDirtyOrInvalid },
    handlers: { handleFormSubmit, handleFormReset },
  }
}
